import { Injectable } from '@angular/core';
import { IListColumnConfig } from '@datachain/ui-sdk/common/base/list-elements.interface';
import { Vault } from '@ultimate/vault';
import { Set } from 'immutable';

import { ProjectStorage } from './project.storage';

type StoredColumn = {
	dataType: string;
	dataField: string;
	fixed: boolean;
	name: string;
	visible: boolean;
	visibleIndex: number;
	filterType: string;
	filterValue: Array<string | number>;
	filterValues: Array<string | number>;
	fixedPosition: boolean;
	selectedFilterOperation: string;
	groupIndex: number;
	sortIndex: number;
	sortOrder: 'asc' | 'desc';
	width: number | string;
};
export type IDxList = {
	allowedPageSizes: Array<number>;
	columns: Array<Partial<StoredColumn>>;
	filterPanel: { filterEnabled: true };
	filterValue: Array<string>;
	pageIndex: number;
	pageSize: number;
	searchText: string;
	selectedRowKeys: Array<number | string>;
};

@Injectable({
	providedIn: 'root',
})
export class DxListsStorage extends Vault {
	private trackedLists = Set<string>();
	public constructor(private readonly userProjectSettings: ProjectStorage) {
		super({
			type: 'local',
		});
	}

	public setListState(listKey: string, state: IDxList): void {
		const projectId = this.userProjectSettings.getProjectId();
		const listKeyByProject = this.generateListKeyWithProjectId(
			listKey,
			projectId
		);
		if (!this.trackedLists.has(listKeyByProject)) {
			this.trackedLists = this.trackedLists.add(listKeyByProject);
		}
		this.set<IDxList>(listKeyByProject, state);
	}

	public getListState(listKey: string): IDxList | undefined {
		const projectId = this.userProjectSettings.getProjectId();
		const listKeyByProject = this.generateListKeyWithProjectId(
			listKey,
			projectId
		);
		const data = this.getList(listKeyByProject);
		const maybe = this.trackedLists.get(listKeyByProject);
		if (!maybe && !data) {
			// eslint-disable-next-line no-console
			console.warn(
				'%c Local storage ',
				'color: #4CAF50; background-color: black; font-weight: bold',
				'no data found and not tracked yet for: ',
				listKeyByProject
			);
		}
		return data;
	}

	public synchronize<T extends IListColumnConfig<U>, U = unknown>(
		listKey: string,
		columns: Array<T>
	): Array<T> {
		const projectId = this.userProjectSettings.getProjectId();
		const listKeyByProject = this.generateListKeyWithProjectId(
			listKey,
			projectId
		);
		const localStorageListState = this.getList(listKeyByProject);
		if (localStorageListState === undefined) {
			return columns;
		}
		return columns.map((generated) => {
			const columnInLocalStorage = localStorageListState.columns.find(
				(c) => c.dataField === generated.field
			);
			if (!columnInLocalStorage) {
				return generated;
			}
			return {
				...generated,
				visible: columnInLocalStorage.visible,
				groupIdx: columnInLocalStorage.groupIndex,
				['selectedFilterOperation']:
					columnInLocalStorage.selectedFilterOperation,
				['filterValue']: columnInLocalStorage.filterValue,
				['filterValues']: columnInLocalStorage.filterValues,
				['width']: columnInLocalStorage.width,
				['sortIndex']: columnInLocalStorage.sortIndex,
				['sortOrder']: columnInLocalStorage.sortOrder,
			};
		});
	}

	private getList(listKey: string): IDxList | undefined {
		return this.get<IDxList>(listKey);
	}

	private generateListKeyWithProjectId(
		listKey: string,
		projectId: number
	): string {
		return `project__${projectId}__${listKey}`;
	}
}
